<template>
  <div class="customer-list">
    <KTCodePreview title="Danh sách khách hàng/nhà cung cấp">
      <!-- Toolbar -->
      <template v-slot:toolbar>
        <div class="row">
          <div>
            <b-dropdown size="sm" right>
              <template #button-content>
                <i style="font-size: 1rem" class="fas fa-cog"></i>
                <b>Thao tác</b>
              </template>
              <b-dropdown-item @click="showExcelModal">
                <i style="font-size: 1rem" class="far fa-file-excel mr-1"></i>
                <span>Xuất Excel</span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <div v-if="checkPermission('CUSTOMER_INSERT')" class="ml-3">
            <router-link to="/customers/add-customer">
              <b-button size="sm" variant="primary">
                <i style="font-size: 1rem" class="flaticon2-add-1"></i>
                <b>Thêm mới</b>
              </b-button>
            </router-link>
          </div>
        </div>
      </template>

      <!-- Filters -->
      <template v-slot:preview>
        <div class="mb-5">
          <b-row class="mb-3">
            <b-col cols="2">
              <b-input
                size="sm"
                placeholder="Tên khách hàng"
                v-model="filters.name"
                v-on:keyup.enter="applyFilters"
              />
            </b-col>
            <b-col cols="2">
              <b-input
                size="sm"
                placeholder="Mã số thuế"
                v-model="filters.taxCode"
                autocomplete="off"
                v-on:keyup.enter="applyFilters"
              />
            </b-col>
            <b-col cols="2">
              <b-input
                size="sm"
                placeholder="Số điện thoại"
                v-model="filters.phone"
                v-focus
                autocomplete="off"
                v-on:keyup.enter="applyFilters"
              />
            </b-col>
            <b-col cols="2">
              <b-form-select
                size="sm"
                v-model="filters.type"
                :options="customerTypeOptions"
                class="select-style"
              >
              </b-form-select>
            </b-col>
            <b-col cols="2">
              <b-form-select
                size="sm"
                v-model="filters.status"
                :options="statusOptions"
                class="select-style"
              >
              </b-form-select>
            </b-col>
            <b-col cols="2">
              <b-form-select
                size="sm"
                v-model="filters.gender"
                :options="genderOptions"
                class="select-style"
              >
                <template v-slot:first>
                  <b-form-select-option :value="null"
                    >Tất cả giới tính</b-form-select-option
                  >
                </template>
              </b-form-select>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="auto">
              <b-button
                class="mr-2"
                style="width: 70px"
                variant="primary"
                size="sm"
                @click="applyFilters"
              >
                Lọc
              </b-button>
              <b-button
                style="width: 70px"
                variant="outline-secondary"
                size="sm"
                @click="resetFilters"
              >
                Xóa lọc
              </b-button>
            </b-col>
          </b-row>
        </div>

        <!-- Customer Table -->
        <div v-if="filters.parentId || filters.id" class="mb-2 text-muted small">
          <span>Đang lọc theo KH/NCC chính:</span>
          <b-badge
            variant="light-primary"
            class="ml-1 cursor-pointer"
            @click="clearFilterByParent"
          >
            {{ filters.parentName || filters.parentId || filters.id }} <i class="fas fa-times ml-1"></i>
          </b-badge>
        </div>

        <b-row>
          <b-col cols="12">
            <b-table
              :items="customers"
              :fields="tableFields"
              bordered
              hover
              :busy="isLoading"
            >
              <template v-slot:table-busy>
                <vcl-table :speed="5" :animate="true" :columns="10"></vcl-table>
              </template>

              <template #cell(index)="data">
                {{ data.index + 1 }}
              </template>

              <template v-slot:cell(fullName)="row">
                <span class="d-inline-flex align-items-center">
                  <!-- KH/NCC phụ (child) -->
                  <span
                    v-if="row.item.parentId != null"
                    v-b-tooltip.hover
                    title="KH/NCC phụ - Click để xem KH/NCC chính"
                    class="mr-1 text-info cursor-pointer provider-parent-icon"
                    @click.stop="onFilterByParent({
                      queryBy: 'id',
                      id: row.item.parentId,
                      name: row.item.parentName || row.item.parent?.fullName,
                    })"
                  >
                    <i class="fas fa-code-branch" style="font-size: 0.9rem"></i>
                  </span>
                  <!-- KH/NCC chính (parent) -->
                  <span
                    v-else
                    v-b-tooltip.hover
                    title="Tìm các KH/NCC phụ của khách hàng này"
                    class="mr-1 text-primary cursor-pointer provider-parent-icon"
                    @click.stop="onFilterByParent({
                      queryBy: 'parentId',
                      id: row.item.id,
                      name: row.item.fullName,
                    })"
                  >
                    <i class="fas fa-building" style="font-size: 0.9rem"></i>
                  </span>
                  <span class="font-weight-bold text-primary">{{ row.item.fullName }}</span>
                </span><br/>
                <span v-if="row.item.taxCode">MST: <span class="font-weight-bold text-warning">{{ row.item.taxCode }}</span><br/></span>
                <span v-if="row.item.taxCode">Mã: <span class="font-weight-bold">{{ row.item.code }}</span><br/></span>
                <span v-if="row.item.identifyNo">CMND/CCCD: <span class="font-weight-bold">{{ row.item.identifyNo }}</span><br/></span>
                <span v-if="row.item.supplierGroupName">Nhóm: <span class="font-weight-bold">{{ row.item.supplierGroupName }}</span></span>
              </template>

              <template v-slot:cell(phoneNo)="row">
                <router-link
                  :to="{
                    name: 'update-customer',
                    query: { id: row.item.id, phoneNo: row.item.phoneNo },
                  }"
                >
                  <b>{{ row.item.phoneNo }}</b>
                </router-link>
              </template>

              <template v-slot:cell(type)="row">
                {{ row.item.typeName }}
              </template>

              <template v-slot:cell(email)="row">
                <span class="text-muted small">{{ row.item.email }}</span>
              </template>

              <template v-slot:cell(totalBuyedAmount)="row">
                {{ formatCurrency(row.item.totalBuyedAmount) }}
              </template>

              <template v-slot:cell(lastedBuyedAt)="row">
                {{ formatDate(row.item.lastedBuyedAt) }}
              </template>

              <template v-slot:cell(status)="row">
                <span
                  v-if="checkStatus(row.item)"
                  class="label font-weight-bold label-lg label-light-success label-inline"
                >
                  Đang GD
                </span>
                <span v-else>
                  <span
                    class="label font-weight-bold label-lg label-light-danger label-inline"
                  >
                    Ngừng GD
                  </span>
                </span>
              </template>

              <!-- Actions -->
              <template v-slot:cell(actions)="row">
                <b-dropdown size="sm" no-caret right>
                  <template #button-content>
                    <i
                      style="font-size: 1rem"
                      class="flaticon2-settings pr-0"
                    ></i>
                  </template>

                  <!-- View / Edit -->
                  <b-dropdown-item
                    @click="viewCustomer(row.item)"
                    v-if="checkViewOnly()"
                  >
                    <span style="color: #3f4254; font-size: 12px">
                      <i style="font-size: 1rem" class="fas fa-eye icon-color"></i>
                      &nbsp; Chi tiết
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    @click="editCustomer(row.item)"
                    v-if="!checkViewOnly()"
                  >
                    <span style="color: #3f4254; font-size: 12px">
                      <i style="font-size: 1rem" class="flaticon2-pen icon-color"></i>
                      &nbsp; Cập nhật
                    </span>
                  </b-dropdown-item>

                  <!-- Toggle status -->
                  <b-dropdown-item
                    v-if="!checkViewOnly()"
                    @click="toggleStatus(row.item)"
                  >
                    <span
                      :style="{
                        color: row.item.status === CUSTOMER_STATUS.ACTIVE ? '#d33' : '#28a745',
                        fontSize: '12px',
                      }"
                    >
                      <i
                        style="font-size: 1rem"
                        :class="row.item.status === CUSTOMER_STATUS.ACTIVE ? 'fas fa-ban' : 'fas fa-check-circle'"
                      ></i>
                      &nbsp;
                      {{ row.item.status === CUSTOMER_STATUS.ACTIVE ? 'Ngưng dùng' : 'Kích hoạt lại' }}
                    </span>
                  </b-dropdown-item>

                  <!-- Assign parent -->
                  <b-dropdown-item
                    v-if="!checkViewOnly()"
                    @click="openAssignParent(row.item)"
                  >
                    <span style="color: #3f4254; font-size: 12px">
                      <i style="font-size: 1rem; color: #7e57c2" class="fas fa-link"></i>
                      &nbsp; Gán NCC chính
                    </span>
                  </b-dropdown-item>

                  <!-- Delete -->
                  <b-dropdown-item
                    @click="confirmDelete(row.item)"
                    v-if="checkPermission('CUSTOMER_DELETE')"
                  >
                    <span style="color: #3f4254; font-size: 12px">
                      <i
                        style="font-size: 1rem; color: #d33"
                        class="flaticon2-rubbish-bin-delete-button"
                      ></i>
                      &nbsp; Xóa
                    </span>
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>
          </b-col>
        </b-row>

        <!-- Pagination -->
        <b-row>
          <b-col>
            <p class="mt-3 text-dark" style="font-weight: 500">
              Tổng số khách hàng: {{ totalItems }}
            </p>
          </b-col>
          <div class="d-flex justify-content-end">
            <b-col>
              <b-pagination-nav
                class="custom-pagination"
                v-if="totalPages >= 2"
                :link-gen="paginationLink"
                :number-of-pages="totalPages"
                use-router
                @change="onPageChanged"
                align="right"
                first-class="page-item-first btn btn-icon btn-sm mr-1 my-1"
                prev-class="page-item-prev btn btn-icon btn-sm mr-.5 my-1"
                next-class="page-item-next btn btn-icon btn-sm mr-1 my-1 ml-.5"
                last-class="page-item-last btn btn-icon btn-sm my-1"
                page-class="btn btn-icon btn-sm border-0 mr-.5 my-1"
              >
                <template v-slot:first-text>
                  <i class="ki ki-bold-double-arrow-back icon-xs"></i>
                </template>
                <template v-slot:prev-text>
                  <i class="ki ki-bold-arrow-back icon-xs"></i>
                </template>
                <template v-slot:next-text>
                  <i class="ki ki-bold-arrow-next icon-xs"></i>
                </template>
                <template v-slot:last-text>
                  <i class="ki ki-bold-double-arrow-next icon-xs"></i>
                </template>
              </b-pagination-nav>
            </b-col>
          </div>
        </b-row>
      </template>
    </KTCodePreview>

    <!-- Excel Export Modal -->
    <excel-export-modal
      ref="excelModalRef"
      :data="customers"
      :fields="exportFields"
      @export="exportToExcel"
    />

    <!-- Assign parent modal -->
    <AssignParentModal
      v-if="assigningCustomer"
      :child-id="assigningCustomer.id"
      :child-name="assigningCustomer.fullName"
      :current-parent-id="assigningCustomer.parentId || null"
      :current-parent-name="assigningCustomer.parentName || ''"
      @assigned="onAssigned"
      @close="assigningCustomer = null"
    />
  </div>
</template>

<script>
import {
  defineComponent,
  ref,
  onMounted,
  watch,
  getCurrentInstance,
} from 'vue';
import KTCodePreview from '@/view/content/CodePreview.vue';
import { VclTable } from 'vue-content-loading';
import Swal from 'sweetalert2';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { checkPermission } from '@/utils/saveDataToLocal';
import { formatDate, convertPrice as formatCurrency } from '@/utils/common';
import useCustomerList from './composables/useCustomerList';
import ExcelExportModal from '@/view/components/common/ExcelExportModal.vue';
import AssignParentModal from './components/AssignParentModal.vue';
import { useRouter } from 'vue2-helpers/vue-router';
import {
  MAP_CUSTOMER_TYPE,
  TABLE_FIELDS,
  EXPORT_EXCEL_FIELDS,
  GENDER_OPTIONS,
  STATUS_OPTIONS,
} from './constants';
import { CUSTOMER_STATUS } from '@/utils/enum';
import { CustomerTypeService } from '@/view/pages/customer-type/service/customer-type.service';

export default defineComponent({
  name: 'ListCustomer',

  components: {
    KTCodePreview,
    VclTable,
    ExcelExportModal,
    AssignParentModal,
  },

  setup() {
    const excelModalRef = ref(null);
    const assigningCustomer = ref(null);
    const customerTypeOptions = ref([{ value: null, text: 'Chọn Nhóm KH/NCC' }]);
    const { proxy } = getCurrentInstance();
    const router = useRouter();

    const {
      customers,
      totalItems,
      totalPages,
      isLoading,
      filters,
      loadCustomers,
      deleteCustomer,
      updateCustomerStatus,
      applyFilters,
      resetFilters,
      paginationLink,
    } = useCustomerList();

    const showExcelModal = () => {
      excelModalRef.value.show();
    };

    const exportToExcel = (options) => {
      console.log('Export to Excel', options);
    };

    const checkViewOnly = () => {
      if (
        checkPermission('CUSTOMER_VIEW') &&
        !checkPermission('CUSTOMER_UPDATE')
      ) {
        return true;
      }
      return false;
    };

    const checkStatus = (item) => {
      return item.status === CUSTOMER_STATUS.ACTIVE;
    };

    const viewCustomer = (customer) => {
      router.push({
        name: 'update-customer',
        query: { id: customer.id, phoneNo: customer.phoneNo },
      });
    };

    const editCustomer = (customer) => {
      router.push({
        name: 'update-customer',
        query: { id: customer.id, phoneNo: customer.phoneNo },
      });
    };

    const toggleStatus = (customer) => {
      const newStatus =
        customer.status === CUSTOMER_STATUS.ACTIVE
          ? CUSTOMER_STATUS.DEACTIVE
          : CUSTOMER_STATUS.ACTIVE;
      const actionLabel = newStatus === CUSTOMER_STATUS.DEACTIVE ? 'Ngưng dùng' : 'Kích hoạt lại';
      const actionText =
        newStatus === CUSTOMER_STATUS.DEACTIVE
          ? 'Bạn có chắc muốn ngưng giao dịch với khách hàng này?'
          : 'Bạn có chắc muốn kích hoạt lại khách hàng này?';

      Swal.fire({
        title: `${actionLabel}?`,
        text: actionText,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: newStatus === CUSTOMER_STATUS.DEACTIVE ? 'rgb(221, 51, 51)' : '#28a745',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: actionLabel,
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
      }).then((result) => {
        if (result.value) {
          updateCustomerStatus(customer.id, newStatus);
        }
      });
    };

    const confirmDelete = (customer) => {
      Swal.fire({
        title: 'Xóa khách hàng!',
        text: 'Bạn có chắc muốn xóa khách hàng này không?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xóa',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
      }).then((result) => {
        if (result.value) {
          deleteCustomer(customer.id);
        }
      });
    };

    const onPageChanged = (page) => {
      loadCustomers(page);
    };

    const openAssignParent = (customer) => {
      assigningCustomer.value = customer;
    };

    const onAssigned = () => {
      assigningCustomer.value = null;
      loadCustomers();
    };

    const clearFilterByParent = () => {
      filters.parentId = null;
      filters.parentName = '';
      filters.id = null;
      filters.queryBy = null;
      applyFilters();
    };

    const onFilterByParent = (payload) => {
      const { id, name, queryBy } = payload;
      filters.parentId = queryBy === 'parentId' ? id : null;
      filters.id = queryBy === 'id' ? id : null;
      filters.parentName = name || '';
      filters.queryBy = queryBy || null;
      applyFilters();
    };

    const loadCustomerTypeOptions = async () => {
      try {
        const res = await CustomerTypeService.selectAll({ isActive: true });
        const types = res?.data?.data || [];
        customerTypeOptions.value = [
          { value: null, text: 'Chọn Nhóm KH/NCC' },
          ...types.map((t) => ({ value: t.id, text: t.name })),
        ];
      } catch {
        // giữ option mặc định
      }
    };

    onMounted(() => {
      proxy.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Khách hàng', route: '/customers' },
        { title: 'Danh sách khách hàng' },
      ]);

      loadCustomerTypeOptions();
      const page = parseInt(router.currentRoute.query.page) || 1;
      loadCustomers(page);
    });

    watch(
      () => router.currentRoute.query.page,
      (newPage) => {
        if (newPage) {
          loadCustomers(parseInt(newPage));
        }
      },
    );

    return {
      customers,
      totalItems,
      totalPages,
      isLoading,
      filters,
      tableFields: TABLE_FIELDS,
      exportFields: EXPORT_EXCEL_FIELDS,
      genderOptions: GENDER_OPTIONS,
      statusOptions: STATUS_OPTIONS,
      customerTypeOptions,
      excelModalRef,
      assigningCustomer,

      applyFilters,
      resetFilters,
      paginationLink,
      checkPermission,
      checkViewOnly,
      viewCustomer,
      editCustomer,
      toggleStatus,
      confirmDelete,
      onPageChanged,
      clearFilterByParent,
      onFilterByParent,
      showExcelModal,
      exportToExcel,
      openAssignParent,
      onAssigned,
      customerTypeMap: MAP_CUSTOMER_TYPE,
      CUSTOMER_STATUS,
      formatDate,
      formatCurrency,
      checkStatus,
    };
  },
});
</script>

<style scoped>
.badge {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px;
}
.badge-success {
  background-color: #e8f5e9;
  color: #2e7d32;
}
.badge-secondary {
  background-color: #f5f5f5;
  color: #757575;
}
.provider-parent-icon:hover {
  opacity: 0.8;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
